/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {Request_TranscribeSpeech, Response_TranscribeSpeech} from "./message.js";
import type {ApiWithBody, ApiWithQuery} from "@intuitionrobotics/thunderstorm";
import type {
    Request_InsertData,
    Request_PatientData,
    Request_PatientDataForMany,
    Request_PatientLastUpdated,
    Response_PatientLastUpdate,
    Request_PatientsLastUpdated,
    ReturnType_PatientData, Response_PatientsLastUpdate
} from "./patient-data.js";
import type {DB_UserRun} from "./cohort.js";
import type {Clause_Where, DB_Object} from "@intuitionrobotics/firebase";
import type {PartialProperties} from "@intuitionrobotics/ts-common";
import type {DB_PatientConfig, MeasurementRange, timeUnits} from "./care_config.js";
import type {DB_User, UserRole} from "@app/ir-q-app-common/types/care-center/user";
import type {ApiTypeBinder} from "@intuitionrobotics/thunderstorm/shared/types";
import type {DB_UnitMetadata} from "@app/ir-q-app-common/types/unit-config";
import type {MedicalScreeningDataResponse} from "./medical-screening.js";
import type {Body_GetReadSignedUrl} from "@app/ir-q-app-common/types/message-api";
import type {DB_Message} from "@app/ir-q-app-common/types/message";

export type Request_AddNewAccount = {
    email: string;
    password?: string;
    password_check?: string;
    user: PartialProperties<DB_User, "_id">,
    alreadyExist?: boolean
}

export type ExcelRow = {
    A?: string,
    B?: string
    C?: string
    D?: number
}

export type Response_PatientDataForMany = {
    result: ExcelRow[]
}

export type ApiGetBigData = ApiWithBody<"/v1/graphs/data", Request_PatientData, ReturnType_PatientData>
export type ApiGetBigDataForMany = ApiWithBody<"/v1/graphs/data/for-many", Request_PatientDataForMany, Response_PatientDataForMany>

export type ApiDeleteBigData = ApiWithBody<"/v1/graphs/delete", Request_InsertData, void>
export type ApiPatientLastUpdated = ApiWithBody<"/v1/graphs/patient-updated", Request_PatientLastUpdated, Response_PatientLastUpdate>
export type ApiPatientsLastUpdated = ApiWithBody<"/v1/graphs/patients-updated", Request_PatientsLastUpdated, Response_PatientsLastUpdate[]>

export type ApiTranscribeSpeech = ApiWithBody<"/v1/speech/transcribe", Request_TranscribeSpeech, Response_TranscribeSpeech>;

export type ApiGetUserRuns = ApiWithQuery<"/v1/cohort/get-user-runs", DB_UserRun[], { patientId: string }>
export type ApiSetUserRuns = ApiWithBody<"/v1/cohort/set-user-runs", DB_UserRun[], DB_UserRun[]>
export type ApiRemoveUserRuns = ApiWithBody<"/v1/cohort/remove-user-runs", Clause_Where<DB_UserRun>, void>

export type AccountApi_AddNewAccount = ApiWithBody<'/v1/account/add-new-account', Request_AddNewAccount, DB_User>;
export type AccountApi_RefreshJWT = ApiWithBody<'/v1/account/refresh-jwt', void, void>;

export type Request_UpsertAccount = {
    email: string
    password: string
    password_check: string
}

export type BodyUpdateUser = DB_Object & Pick<DB_User, "first" | "last" | "title" | "mailEnabled" | "roles">;
export type ApiClearMessages = ApiWithBody<"/v1/message/clear", { unitId: string }, void>
export type ApiUserUnsubscribe = ApiWithQuery<"/v1/user/unsubscribe", void>
export type ApiUserChangePassword = ApiWithBody<"/v1/user/change-password", Request_UpsertAccount, void>
export type ApiListPatientConfigs = ApiWithQuery<"/v1/config/list", DB_PatientConfig[]>
export type ApiCreatePatientConfigs = ApiWithBody<"/v1/config/create", DB_PatientConfig, DB_PatientConfig>
export type ApiPublishEvents = ApiWithBody<"/v1/user/user-events", BaseEvent[], void>
export type ApiUsersUpdate = ApiWithBody<"/v1/user/update", BodyUpdateUser, DB_User>

export const timeUnitToWindowType: { [k in (typeof timeUnits)[number]]: (typeof windowType)[number] } = {
    day: "DAY",
    week: "WEEK",
    month: "MONTH"
}
export const windowType = [
    "DAY",
    "WEEK",
    "MONTH"
] as const;

export type CareGoal = {
    name: string
    windowType: typeof windowType[number],
    threshold: number
}

export type UICareGoal = CareGoal & {
    ranges: MeasurementRange
}

export type UnitCareGoal = UICareGoal & {
    unitId: string
    enabled: boolean
}

// Base type containing the common fields
export type BaseEvent = {
    id: string;
    factType: string;
    timeStamp: number;
    timeZone?: string;
    creator: string
};

// CareManagerActivityEvent extends the base type
export type CareManagerActivityEvent = BaseEvent & {
    userId: string;
    objectId?: string;
    activityDetails?: string;
    objectType: string;
    objectSubType?: string;
    sectionId: string;
    activityStatus: string;
    entity: string;
    frequency?: string;
    frequencyValue?: number;
};

// CareManagerEvent extends the base type
export type CareManagerEvent = BaseEvent & {
    userId: string;
    email: string;
    userName: string;
    roles?: UserRole[];
    status: string;
    activeStatus: string;
    startDate?: number;
    environment: string
};

export type ScreeningRequestEvent = BaseEvent & {
    activityDetails: string,
    objectType: "hedis",
    objectSubType: "screening_request",
    activityStatues: "yes" | "no",
}

export type ApiListUnitGoals = ApiTypeBinder<"/v1/goal/list", UICareGoal[], void, { unitId: string }>
export type ApiCreateUnitGoal = ApiWithBody<"/v1/goal/upsert", UnitCareGoal, void>

export type CareCenterMetadata = ApiWithBody<"/v1/metadata/query", Clause_Where<DB_UnitMetadata<any>>, DB_UnitMetadata<any>[]>

export type Api_GetMedicalScreenings = ApiWithQuery<"/v1/medical-screening/get", MedicalScreeningDataResponse, { agentId: string }>
export type Api_SetScreeningEnabled = ApiWithBody<"/v1/medical-screening/set-enabled", {
    agentId: string;
    screeningId: number;
    enabled: boolean;
}, void>

export type Request_FallRisk = { unitId: string }
export type Response_FallRisk = { fallRisk: boolean | null, timestamp: number | null }
export type Api_GetFallRisk = ApiWithBody<"/v1/patient/fall-risk", Request_FallRisk, Response_FallRisk>
// Care Center's own body for the shared /v1/message/get-read-signed-url route. ir-q-messaging's
// `Body_GetReadSignedUrl` is `{messageId}` only — nothing to authorize against — so CC's route
// additionally requires the chat and its group contact: `senderId` gives
// customGroupContactMiddleWare a groupContactId to gate on, and `chatId` lets the handler prove
// the message actually lives in a chat that group participates in. Declared here rather than
// widening the shared type: messaging's own route still takes `{messageId}`.
export type Body_CcGetReadSignedUrl = Body_GetReadSignedUrl & { chatId: string, senderId: string }
export type Api_CcGetReadSignedUrl = ApiWithBody<"/v1/message/get-read-signed-url", Body_CcGetReadSignedUrl, DB_Message<any>>
